/**
 * ================================================================
 * CONTEXT DE PERMISSÕES
 * Gerencia estado global de permissões do usuário
 * Data: 03/11/2025
 * ================================================================
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { authService } from '../services/authService';

interface PermissionContextData {
  permissoes: any;
  cargo: string;
  tipoEmpresa: string;
  carregando: boolean;
  podeAcessar: (recurso: string, acao: string) => boolean;
  carregarPermissoes: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextData>({} as PermissionContextData);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissoes, setPermissoes] = useState<any>({});
  const [cargo, setCargo] = useState<string>('');
  const [tipoEmpresa, setTipoEmpresa] = useState<string>('');
  const [carregando, setCarregando] = useState<boolean>(true);
  const missingLoggedRef = useRef<Set<string>>(new Set());

  // Estrutura padrão de permissões para evitar ruído no console e chaves ausentes
  const DEFAULT_PERMISSIONS: Record<string, Record<string, boolean>> = {
    pacientes: {
      criar: false,
      ver_todos: false,
      editar_todos: false,
      deletar: false
    },
    consultas: {
      criar: false,
      ver_todas: false,
      editar_todas: false,
      cancelar: false
    },
    prontuarios: {
      criar: false,
      ver_todos: false,
      editar_todos: false,
      deletar: false
    },
    financeiro: {
      ver: false,
      registrar: false,
      relatorios: false
    },
    usuarios: {
      criar: false,
      editar: false,
      deletar: false,
      ver_todos: false
    },
    configuracoes: {
      empresa: false,
      sistema: false
    },
    relatorios: {
      ver_todos: false,
      consolidados: false
    },
    sophygames: {
      ver: false,
      jogar: false
    },
    sophyteca: {
      ver: false,
      admin: false
    }
  };

  const normalizePermissions = (raw: any): any => {
    // Mescla rasa por recurso, preservando as ações conhecidas como padrão
    const result: any = { ...DEFAULT_PERMISSIONS };
    if (raw && typeof raw === 'object') {
      for (const recurso of Object.keys(raw)) {
        const recKey = String(recurso);
        const defaults = (DEFAULT_PERMISSIONS as any)[recKey] || {};
        result[recKey] = { ...defaults, ...(raw as any)[recKey] };
      }
    }
    return result;
  };
  
  useEffect(() => {
    // Garantir que a sessão esteja pronta antes de carregar permissões
    let mounted = true;
    (async () => {
      await authService.waitForInitialization();
      if (mounted) {
        await carregarPermissoes();
      }
    })();

    // Ouvir mudanças de autenticação (login/logout) para recarregar permissões
    const onAuthChange = (e: any) => {
      const tipo = e?.detail?.type;
      if (tipo === 'login') {
        carregarPermissoes();
      } else if (tipo === 'logout') {
        setPermissoes(DEFAULT_PERMISSIONS);
        setCargo('');
        setTipoEmpresa('');
      }
    };
    window.addEventListener('authChange', onAuthChange as any);
    return () => {
      mounted = false;
      window.removeEventListener('authChange', onAuthChange as any);
    };
  }, []);
  
  const carregarPermissoes = async () => {
    try {
      setCarregando(true);
      
      // Esperar inicialização e tentar obter token de forma segura
      await authService.waitForInitialization();
      const token = authService.getToken() || sessionStorage.getItem('token');
      if (!token) {
        // Sem token: provavelmente em tela pública ou antes do login; manter defaults
        console.debug('[PERMISSIONS] Usuário não autenticado no momento do carregamento');
        setPermissoes(DEFAULT_PERMISSIONS);
        setCargo('');
        setTipoEmpresa('');
        setCarregando(false);
        return;
      }
      
      const response = await api.get('/auth/permissoes');
      
      if (response.data.sucesso) {
        console.debug('[PERMISSIONS] Permissões carregadas:', response.data.dados);
        const serverPerms = response.data.dados.permissoes || {};
        setPermissoes(normalizePermissions(serverPerms));
        setCargo(response.data.dados.cargo || '');
        setTipoEmpresa(response.data.dados.tipoEmpresa || '');
      }
    } catch (error) {
      console.error('[PERMISSIONS] Erro ao carregar permissões:', error);
      // Definir valores padrão em caso de erro
      setPermissoes(normalizePermissions({}));
      setCargo('');
      setTipoEmpresa('');
    } finally {
      setCarregando(false);
    }
  };
  
  const podeAcessar = (recurso: string, acao: string): boolean => {
    // Se ainda está carregando, retornar silenciosamente falso
    if (carregando) return false;

    const stripEdgeQuotes = (value: string) => value.replace(/^['"]/, '').replace(/['"]$/, '');

    // Normalizar entrada (remover aspas acidentais e usar lowercase)
    const recKey = stripEdgeQuotes(String(recurso).trim()).toLowerCase();
    const acaoKey = stripEdgeQuotes(String(acao).trim()).toLowerCase();

    if (!permissoes[recKey]) {
      const already = missingLoggedRef.current.has(recKey);
      if (!already) {
        console.warn('[PERMISSIONS] Recurso não encontrado:', recKey);
        missingLoggedRef.current.add(recKey);
      }
      return false;
    }

    const temPermissao = !!permissoes[recKey][acaoKey];

    // Reduzir ruído: usar debug em vez de log padrão
    console.debug('[PERMISSIONS] Verificação:', {
      recurso: recKey,
      acao: acaoKey,
      temPermissao,
      cargo
    });

    return temPermissao;
  };
  
  return (
    <PermissionContext.Provider value={{
      permissoes,
      cargo,
      tipoEmpresa,
      carregando,
      podeAcessar,
      carregarPermissoes
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de PermissionProvider');
  }
  
  return context;
};
